import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Trophy, Users, Swords, Trash2, Edit, Eye, EyeOff, Plus, Save } from 'lucide-react';
import { toast } from 'sonner';

const PKBattleManagement = () => {
  const [config, setConfig] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [rounds, setRounds] = useState([]);
  const [selectedRound, setSelectedRound] = useState(null);
  const [currentWinners, setCurrentWinners] = useState(null);
  const [winnersHistory, setWinnersHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newRoundNumber, setNewRoundNumber] = useState('');
  const [newRoundDate, setNewRoundDate] = useState('');
  const [newRoundStartTime, setNewRoundStartTime] = useState('');
  const [battleInterval, setBattleInterval] = useState('10');
  
  // Estados para seleção múltipla
  const [selectedInscribed, setSelectedInscribed] = useState([]);
  const [selectedCancelled, setSelectedCancelled] = useState([]);
  const [selectedDisqualified, setSelectedDisqualified] = useState([]);
  
  // Estados para edição de match
  const [editingMatch, setEditingMatch] = useState(null);
  const [editMatchData, setEditMatchData] = useState({});

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');

  useEffect(() => {
    loadAll();
  }, []);

  useEffect(() => {
    // Selecionar ÚLTIMA rodada automaticamente quando carregar
    if (rounds.length > 0 && !selectedRound) {
      setSelectedRound(rounds[rounds.length - 1].round_number);
    }
  }, [rounds]);

  const loadAll = async () => {
    setLoading(true);
    await Promise.all([loadConfig(), loadParticipants(), loadRounds(), loadCurrentWinners(), loadWinnersHistory()]);
    setLoading(false);
  };

  const loadCurrentWinners = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/pk-battle/winners/current-with-data`);
      setCurrentWinners(response.data);
    } catch (error) {
      console.error('Erro ao carregar ganhadores:', error);
    }
  };

  const loadWinnersHistory = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/pk-battle/winners/history`);
      setWinnersHistory(response.data);
    } catch (error) {
      console.error('Erro ao carregar histórico:', error);
    }
  };

  const removeWinners = async () => {
    if (!window.confirm('Remover os ganhadores atuais?')) return;

    try {
      await axios.delete(`${BACKEND_URL}/api/pk-battle/winners/current`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Ganhadores removidos');
      loadCurrentWinners();
      loadConfig();
    } catch (error) {
      console.error('Erro ao remover:', error);
      toast.error('Erro ao remover ganhadores');
    }
  };

  const archiveWinners = async () => {
    if (!window.confirm('Arquivar os ganhadores atuais no histórico?')) return;

    // Gerar data automática no formato "DD/MM/YYYY"
    const now = new Date();
    const eventDate = now.toLocaleDateString('pt-BR');

    try {
      await axios.post(
        `${BACKEND_URL}/api/pk-battle/winners/archive`,
        null,
        {
          params: { event_date: eventDate },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      toast.success('Ganhadores arquivados no histórico!');
      loadCurrentWinners();
      loadWinnersHistory();
      loadConfig();
    } catch (error) {
      console.error('Erro ao arquivar:', error);
      const errorMsg = error.response?.data?.detail;
      const displayError = typeof errorMsg === 'string' ? errorMsg : 'Erro ao arquivar ganhadores';
      toast.error(displayError);
    }
  };

  const deleteHistoryEdition = async (editionId, eventDate) => {
    if (!window.confirm(`Excluir a edição de ${eventDate}?`)) return;

    try {
      await axios.delete(
        `${BACKEND_URL}/api/pk-battle/winners/history/${editionId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Edição removida');
      loadWinnersHistory();
    } catch (error) {
      console.error('Erro ao excluir:', error);
      toast.error('Erro ao excluir edição');
    }
  };

  const loadConfig = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/pk-battle/config`);
      setConfig(response.data);
    } catch (error) {
      console.error('Erro ao carregar config:', error);
      toast.error('Erro ao carregar configuração');
    }
  };

  const loadParticipants = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/pk-battle/participants`);
      setParticipants(response.data);
    } catch (error) {
      console.error('Erro ao carregar participantes:', error);
    }
  };

  const loadRounds = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/pk-battle/rounds`);
      setRounds(response.data);
    } catch (error) {
      console.error('Erro ao carregar rodadas:', error);
    }
  };

  const updateConfig = async (field, value) => {
    try {
      const updatedConfig = { ...config, [field]: value };
      await axios.put(
        `${BACKEND_URL}/api/pk-battle/config`,
        updatedConfig,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setConfig(updatedConfig);
      toast.success('Configuração atualizada');
    } catch (error) {
      console.error('Erro ao atualizar config:', error);
      const errorMsg = error.response?.data?.detail;
      const displayError = typeof errorMsg === 'string' ? errorMsg : 
                          Array.isArray(errorMsg) ? errorMsg.map(e => e.msg || e).join(', ') : 
                          'Erro ao atualizar configuração';
      toast.error(displayError);
    }
  };

  const removeFromCancelled = async (kakoId) => {
    try {
      await axios.delete(
        `${BACKEND_URL}/api/pk-battle/participants/${kakoId}/remove-cancellation`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Removido da lista de cancelados');
      loadParticipants();
    } catch (error) {
      console.error('Erro ao remover:', error);
      const errorMsg = error.response?.data?.detail;
      const displayError = typeof errorMsg === 'string' ? errorMsg : 
                          Array.isArray(errorMsg) ? errorMsg.map(e => e.msg || e).join(', ') : 
                          'Erro ao remover participante';
      toast.error(displayError);
    }
  };

  const removeParticipant = async (kakoId, statusName) => {
    if (!window.confirm(`Tem certeza que deseja remover este participante da lista?`)) {
      return;
    }

    try {
      await axios.delete(
        `${BACKEND_URL}/api/pk-battle/participants/${kakoId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Participante removido com sucesso');
      loadParticipants();
    } catch (error) {
      console.error('Erro ao remover:', error);
      const errorMsg = error.response?.data?.detail;
      const displayError = typeof errorMsg === 'string' ? errorMsg : 
                          Array.isArray(errorMsg) ? errorMsg.map(e => e.msg || e).join(', ') : 
                          'Erro ao remover participante';
      toast.error(displayError);
    }
  };

  const removeSelectedParticipants = async (selectedList, listName) => {
    if (selectedList.length === 0) {
      toast.error('Nenhum participante selecionado');
      return;
    }

    if (!window.confirm(`Excluir ${selectedList.length} participante(s) selecionado(s)?`)) {
      return;
    }

    try {
      await Promise.all(
        selectedList.map(kakoId =>
          axios.delete(`${BACKEND_URL}/api/pk-battle/participants/${kakoId}`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        )
      );
      toast.success(`${selectedList.length} participante(s) removido(s)`);
      
      // Limpar seleção
      if (listName === 'inscribed') setSelectedInscribed([]);
      else if (listName === 'cancelled') setSelectedCancelled([]);
      else if (listName === 'disqualified') setSelectedDisqualified([]);
      
      loadParticipants();
    } catch (error) {
      console.error('Erro ao remover:', error);
      toast.error('Erro ao remover participantes');
    }
  };

  const toggleSelectAll = (list, selectedList, setSelectedList) => {
    if (selectedList.length === list.length) {
      setSelectedList([]);
    } else {
      setSelectedList(list.map(p => p.kako_id));
    }
  };

  const toggleSelect = (kakoId, selectedList, setSelectedList) => {
    if (selectedList.includes(kakoId)) {
      setSelectedList(selectedList.filter(id => id !== kakoId));
    } else {
      setSelectedList([...selectedList, kakoId]);
    }
  };

  const createRound = async () => {
    if (!newRoundNumber) {
      toast.error('Informe o número da rodada');
      return;
    }

    // Combinar data e hora
    let fullDateTime = '';
    if (newRoundDate && newRoundStartTime) {
      fullDateTime = `${newRoundDate} às ${newRoundStartTime}`;
    } else if (newRoundStartTime) {
      fullDateTime = newRoundStartTime;
    }

    try {
      await axios.post(
        `${BACKEND_URL}/api/pk-battle/rounds/create`,
        {
          round_number: parseInt(newRoundNumber),
          round_datetime: fullDateTime || null,
          battle_interval_minutes: parseInt(battleInterval) || 10
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Rodada criada com sucesso!');
      setNewRoundNumber('');
      setNewRoundDate('');
      setNewRoundStartTime('');
      loadRounds();
    } catch (error) {
      console.error('Erro ao criar rodada:', error);
      const errorMsg = error.response?.data?.detail;
      const displayError = typeof errorMsg === 'string' ? errorMsg : 
                          Array.isArray(errorMsg) ? errorMsg.map(e => e.msg || e).join(', ') : 
                          'Erro ao criar rodada';
      toast.error(displayError);
    }
  };

  const deleteRound = async (roundNumber) => {
    if (!window.confirm(`Excluir rodada ${roundNumber}?`)) return;

    try {
      await axios.delete(
        `${BACKEND_URL}/api/pk-battle/rounds/${roundNumber}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Rodada excluída');
      
      // Resetar rodada selecionada se foi a que excluiu
      if (selectedRound === roundNumber) {
        setSelectedRound(null);
      }
      
      // Aguardar e recarregar
      await loadRounds();
    } catch (error) {
      console.error('Erro ao excluir rodada:', error);
      const errorMsg = error.response?.data?.detail;
      const displayError = typeof errorMsg === 'string' ? errorMsg : 
                          Array.isArray(errorMsg) ? errorMsg.map(e => e.msg || e).join(', ') : 
                          'Erro ao excluir rodada';
      toast.error(displayError);
    }
  };

  const toggleRoundVisibility = async (roundNumber, currentVisibility) => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/pk-battle/rounds/${roundNumber}/visibility`,
        null,
        {
          params: { show_to_public: !currentVisibility },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      toast.success('Visibilidade atualizada');
      loadRounds();
    } catch (error) {
      console.error('Erro ao alterar visibilidade:', error);
      const errorMsg = error.response?.data?.detail;
      const displayError = typeof errorMsg === 'string' ? errorMsg : 
                          Array.isArray(errorMsg) ? errorMsg.map(e => e.msg || e).join(', ') : 
                          'Erro ao alterar visibilidade';
      toast.error(displayError);
    }
  };

  const startEditMatch = (match) => {
    setEditingMatch(match.match_id);
    setEditMatchData({
      points_participant1: match.points_participant1 || 0,
      points_participant2: match.points_participant2 || 0,
      status: match.status,
      battle_datetime: match.battle_datetime || '',
      participant1_kako_id: match.participant1_kako_id,
      participant2_kako_id: match.participant2_kako_id
    });
  };

  const saveMatch = async (matchId) => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/pk-battle/matches/${matchId}`,
        editMatchData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Match atualizado');
      setEditingMatch(null);
      await loadRounds(); // Recarregar para mostrar mudanças
      await loadParticipants(); // Recarregar participantes (caso WO tenha desclassificado alguém)
    } catch (error) {
      console.error('Erro ao atualizar match:', error);
      const errorMsg = error.response?.data?.detail;
      const displayError = typeof errorMsg === 'string' ? errorMsg : 
                          Array.isArray(errorMsg) ? errorMsg.map(e => e.msg || e).join(', ') : 
                          'Erro ao atualizar match';
      toast.error(displayError);
    }
  };

  const setMatchWO = async (matchId, participant1KakoId, participant2KakoId) => {
    const p1Name = participants.find(p => p.kako_id === participant1KakoId)?.nickname || participant1KakoId;
    const p2Name = participants.find(p => p.kako_id === participant2KakoId)?.nickname || participant2KakoId;

    const woOption = window.prompt(
      `Selecione a situação de WO:\n\n` +
      `1 - ${p1Name} faltou (${p2Name} classificado)\n` +
      `2 - ${p2Name} faltou (${p1Name} classificado)\n` +
      `3 - Ambos faltaram (ambos desclassificados)\n` +
      `4 - Ambos justificaram (ambos classificam)\n\n` +
      `Digite 1, 2, 3 ou 4:`
    );

    if (!woOption || !['1', '2', '3', '4'].includes(woOption)) {
      toast.error('Seleção inválida');
      return;
    }

    try {
      // Opção 1 ou 2: WO normal (um falta)
      if (woOption === '1' || woOption === '2') {
        const selectedKakoId = woOption === '1' ? participant1KakoId : participant2KakoId;
        
        await axios.put(
          `${BACKEND_URL}/api/pk-battle/matches/${matchId}/wo`,
          null,
          {
            params: { wo_participant_kako_id: selectedKakoId },
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        toast.success('WO registrado e participante desclassificado');
      }
      // Opção 3: Ambos faltaram
      else if (woOption === '3') {
        await axios.put(
          `${BACKEND_URL}/api/pk-battle/matches/${matchId}/wo-special`,
          null,
          {
            params: { wo_type: 'both_disqualified' },
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        toast.success('Ambos participantes desclassificados');
      }
      // Opção 4: Ambos justificaram
      else if (woOption === '4') {
        await axios.put(
          `${BACKEND_URL}/api/pk-battle/matches/${matchId}/wo-special`,
          null,
          {
            params: { wo_type: 'both_advance' },
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        toast.success('Ambos participantes classificados para próxima rodada');
      }
      
      loadRounds();
      loadParticipants();
    } catch (error) {
      console.error('Erro ao registrar WO:', error);
      const errorMsg = error.response?.data?.detail;
      const displayError = typeof errorMsg === 'string' ? errorMsg : 
                          Array.isArray(errorMsg) ? errorMsg.map(e => e.msg || e).join(', ') : 
                          'Erro ao registrar WO';
      toast.error(displayError);
    }
  };

  const inscritosAtivos = participants.filter(p => p.status === 'inscrito');
  const cancelados = participants.filter(p => p.status === 'cancelado');
  const desclassificados = participants.filter(p => p.status === 'desclassificado');

  if (loading) {
    return <div className="text-center py-8">Carregando...</div>;
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Swords className="w-6 h-6" />
            Gerenciamento - BATALHA DE PK
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="config" className="w-full">
            <TabsList className="grid w-full grid-cols-5">
              <TabsTrigger value="config">⚙️ Configurações</TabsTrigger>
              <TabsTrigger value="inscriptions">📋 Inscrições ({inscritosAtivos.length})</TabsTrigger>
              <TabsTrigger value="rounds">🎮 Rodadas ({rounds.length})</TabsTrigger>
              <TabsTrigger value="winners">🏆 Ganhadores</TabsTrigger>
              <TabsTrigger value="rules">📜 Regras</TabsTrigger>
            </TabsList>

            {/* TAB: CONFIGURAÇÕES */}
            <TabsContent value="config" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">⚙️ Configurações de Visibilidade</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label>Mostrar Aba de Inscrição para o Público</Label>
                    <Switch
                      checked={config?.show_inscription_tab || false}
                      onCheckedChange={(checked) => updateConfig('show_inscription_tab', checked)}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label>Mostrar Aba de Rodadas para o Público</Label>
                    <Switch
                      checked={config?.show_rounds_tab || false}
                      onCheckedChange={(checked) => updateConfig('show_rounds_tab', checked)}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label>Mostrar Aba de Ganhadores para o Público</Label>
                    <Switch
                      checked={config?.show_winners_tab || false}
                      onCheckedChange={(checked) => updateConfig('show_winners_tab', checked)}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label>Mostrar Aba de Regras para o Público</Label>
                    <Switch
                      checked={config?.show_rules_tab || false}
                      onCheckedChange={(checked) => updateConfig('show_rules_tab', checked)}
                    />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">📝 Controle de Inscrições</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <Label>Inscrições Abertas</Label>
                    <Switch
                      checked={config?.inscriptions_open || false}
                      onCheckedChange={(checked) => updateConfig('inscriptions_open', checked)}
                    />
                  </div>
                  <p className="text-sm text-gray-500 mt-2">
                    {config?.inscriptions_open ? '✅ Hosts podem se inscrever' : '🔒 Inscrições fechadas'}
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">🏆 Prêmios</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <Label>Prêmio TOP 1</Label>
                    <Input
                      value={config?.prize_top1 || ''}
                      onChange={(e) => setConfig({ ...config, prize_top1: e.target.value })}
                      placeholder="Ex: R$ 5.000"
                    />
                  </div>
                  <div>
                    <Label>Prêmio TOP 2</Label>
                    <Input
                      value={config?.prize_top2 || ''}
                      onChange={(e) => setConfig({ ...config, prize_top2: e.target.value })}
                      placeholder="Ex: R$ 3.000"
                    />
                  </div>
                  <Button onClick={async () => {
                    await updateConfig('prize_top1', config.prize_top1);
                    await updateConfig('prize_top2', config.prize_top2);
                  }}>
                    💾 Salvar Prêmios
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>

            {/* TAB: INSCRIÇÕES */}
            <TabsContent value="inscriptions" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Users className="w-5 h-5" />
                      Inscritos Ativos ({inscritosAtivos.length})
                    </div>
                    {inscritosAtivos.length > 0 && (
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => toggleSelectAll(inscritosAtivos, selectedInscribed, setSelectedInscribed)}>
                          {selectedInscribed.length === inscritosAtivos.length ? 'Desmarcar Todos' : 'Selecionar Todos'}
                        </Button>
                        {selectedInscribed.length > 0 && (
                          <Button size="sm" variant="destructive" onClick={() => removeSelectedParticipants(selectedInscribed, 'inscribed')}>
                            🗑️ Excluir ({selectedInscribed.length})
                          </Button>
                        )}
                      </div>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {inscritosAtivos.length === 0 ? (
                    <p className="text-gray-500">Nenhum inscrito ainda</p>
                  ) : (
                    <div className="space-y-2">
                      {inscritosAtivos.map((p) => (
                        <div key={p.kako_id} className="flex items-center gap-3 p-3 border rounded-lg">
                          <Checkbox
                            checked={selectedInscribed.includes(p.kako_id)}
                            onCheckedChange={() => toggleSelect(p.kako_id, selectedInscribed, setSelectedInscribed)}
                          />
                          {p.avatar_url && (
                            <img src={p.avatar_url} alt={p.nickname} className="w-10 h-10 rounded-full" />
                          )}
                          <div className="flex-1">
                            <p className="font-semibold">{p.nickname}</p>
                            <button 
                              onClick={() => {
                                navigator.clipboard.writeText(p.kako_id);
                                toast.success('ID copiado!');
                              }}
                              className="text-sm text-blue-600 hover:text-blue-800 cursor-pointer underline"
                              title="Clique para copiar ID"
                            >
                              ID: {p.kako_id}
                            </button>
                            {p.whatsapp ? (
                              <button 
                                onClick={() => {
                                  navigator.clipboard.writeText(p.whatsapp);
                                  toast.success('WhatsApp copiado!');
                                }}
                                className="block text-sm text-green-600 hover:text-green-800 cursor-pointer underline mt-1"
                                title="Clique para copiar WhatsApp"
                              >
                                📱 {p.whatsapp}
                              </button>
                            ) : (
                              <p className="text-xs text-gray-400 mt-1">SEM TELEFONE</p>
                            )}
                          </div>
                          <Badge className="bg-green-500">Inscrito</Badge>
                          <Button 
                            size="sm" 
                            variant="destructive" 
                            onClick={() => removeParticipant(p.kako_id, 'inscrito')}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center justify-between">
                    <div>❌ Cancelados ({cancelados.length})</div>
                    {cancelados.length > 0 && (
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => toggleSelectAll(cancelados, selectedCancelled, setSelectedCancelled)}>
                          {selectedCancelled.length === cancelados.length ? 'Desmarcar Todos' : 'Selecionar Todos'}
                        </Button>
                        {selectedCancelled.length > 0 && (
                          <Button size="sm" variant="destructive" onClick={() => removeSelectedParticipants(selectedCancelled, 'cancelled')}>
                            🗑️ Excluir ({selectedCancelled.length})
                          </Button>
                        )}
                      </div>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {cancelados.length === 0 ? (
                    <p className="text-gray-500">Nenhum cancelamento</p>
                  ) : (
                    <div className="space-y-2">
                      {cancelados.map((p) => (
                        <div key={p.kako_id} className="flex items-center gap-3 p-3 border rounded-lg">
                          <Checkbox
                            checked={selectedCancelled.includes(p.kako_id)}
                            onCheckedChange={() => toggleSelect(p.kako_id, selectedCancelled, setSelectedCancelled)}
                          />
                          <div className="flex-1">
                            <p className="font-semibold">{p.nickname}</p>
                            <p className="text-sm text-gray-500">
                              Cancelado em: {new Date(p.cancelled_at).toLocaleString('pt-BR')}
                            </p>
                          </div>
                          <Badge className="bg-orange-500">Cancelado</Badge>
                          <Button size="sm" variant="outline" onClick={() => removeFromCancelled(p.kako_id)}>
                            Remover
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center justify-between">
                    <div>🚫 Desclassificados ({desclassificados.length})</div>
                    {desclassificados.length > 0 && (
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => toggleSelectAll(desclassificados, selectedDisqualified, setSelectedDisqualified)}>
                          {selectedDisqualified.length === desclassificados.length ? 'Desmarcar Todos' : 'Selecionar Todos'}
                        </Button>
                        {selectedDisqualified.length > 0 && (
                          <Button size="sm" variant="destructive" onClick={() => removeSelectedParticipants(selectedDisqualified, 'disqualified')}>
                            🗑️ Excluir ({selectedDisqualified.length})
                          </Button>
                        )}
                      </div>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {desclassificados.length === 0 ? (
                    <p className="text-gray-500">Nenhum desclassificado</p>
                  ) : (
                    <div className="space-y-2">
                      {desclassificados.map((p) => (
                        <div key={p.kako_id} className="flex items-center gap-3 p-3 border rounded-lg">
                          <Checkbox
                            checked={selectedDisqualified.includes(p.kako_id)}
                            onCheckedChange={() => toggleSelect(p.kako_id, selectedDisqualified, setSelectedDisqualified)}
                          />
                          <div className="flex-1">
                            <p className="font-semibold">{p.nickname}</p>
                            <p className="text-sm text-gray-500">
                              Desclassificado em: {new Date(p.disqualified_at).toLocaleString('pt-BR')}
                            </p>
                          </div>
                          <Badge className="bg-red-500">Desclassificado</Badge>
                          <Button 
                            size="sm" 
                            variant="destructive" 
                            onClick={() => removeParticipant(p.kako_id, 'desclassificado')}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* TAB: RODADAS */}
            <TabsContent value="rounds" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">➕ Criar Nova Rodada</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <Label>Número da Rodada</Label>
                    <Input
                      type="number"
                      value={newRoundNumber}
                      onChange={(e) => setNewRoundNumber(e.target.value)}
                      placeholder="Ex: 1"
                    />
                  </div>
                  <div>
                    <Label>Data da Rodada</Label>
                    <Input
                      value={newRoundDate}
                      onChange={(e) => setNewRoundDate(e.target.value)}
                      placeholder="Ex: 15/01/2026"
                    />
                  </div>
                  <div>
                    <Label>Hora de Início da Primeira Batalha</Label>
                    <Input
                      value={newRoundStartTime}
                      onChange={(e) => setNewRoundStartTime(e.target.value)}
                      placeholder="Ex: 20:00"
                    />
                  </div>
                  <div>
                    <Label>Intervalo entre Batalhas (minutos)</Label>
                    <Input
                      type="number"
                      value={battleInterval}
                      onChange={(e) => setBattleInterval(e.target.value)}
                      placeholder="Ex: 10"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Cada batalha será {battleInterval || 10} minutos após a anterior
                    </p>
                  </div>
                  <Button onClick={createRound} className="w-full">
                    <Plus className="w-4 h-4 mr-2" />
                    Criar Rodada (Emparelhamento Automático)
                  </Button>
                </CardContent>
              </Card>

              {rounds.length === 0 ? (
                <p className="text-gray-500 text-center py-8">Nenhuma rodada criada ainda</p>
              ) : (
                <>
                  {/* Menu Dropdown de Rodadas */}
                  <div className="mb-4">
                    <select 
                      className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 bg-white font-semibold text-base focus:border-purple-500 focus:ring-2 focus:ring-purple-200"
                      value={selectedRound || ''}
                      onChange={(e) => setSelectedRound(parseInt(e.target.value))}
                    >
                      {rounds.map((round) => (
                        <option key={round.round_number} value={round.round_number}>
                          🎮 Rodada {round.round_number} {round.round_datetime && `- ${round.round_datetime}`}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Controles da Rodada Selecionada */}
                  {selectedRound && (
                    <>
                      <div className="flex gap-2 justify-end">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => toggleRoundVisibility(selectedRound, rounds.find(r => r.round_number === selectedRound)?.show_to_public)}
                          >
                            {rounds.find(r => r.round_number === selectedRound)?.show_to_public ? <><Eye className="w-4 h-4 mr-1" /> Visível</> : <><EyeOff className="w-4 h-4 mr-1" /> Oculto</>}
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => deleteRound(selectedRound)}
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Excluir Rodada
                          </Button>
                        </div>

                        {/* Matches da Rodada Selecionada */}
                        {rounds.find(r => r.round_number === selectedRound)?.matches.map((match) => {
                            const isEditing = editingMatch === match.match_id;
                            const p1Data = match.participant1_data;
                            const p2Data = match.participant2_data;
                            const byeData = match.bye_participant_data;

                            // Card de BYE
                            if (match.is_bye) {
                              return (
                                <Card key={match.match_id} className="bg-green-50 border-green-300 overflow-hidden">
                                  <CardContent className="p-4">
                                    <div className="flex items-center gap-3">
                                      {byeData?.avatar_url && (
                                        <img src={byeData.avatar_url} alt="" className="w-12 h-12 rounded-full" />
                                      )}
                                      <div className="flex-1">
                                        <p className="font-bold">{byeData?.nickname || 'Desconhecido'}</p>
                                        <p className="text-sm text-gray-600">#{byeData?.kako_id}</p>
                                      </div>
                                      <Badge className="bg-green-600">Classificado Automaticamente (BYE)</Badge>
                                    </div>
                                  </CardContent>
                                </Card>
                              );
                            }

                        // Card de Dupla Normal
                        const totalPoints = (match.points_participant1 || 0) + (match.points_participant2 || 0);
                        
                        // Calcular porcentagem com garantia de mínimo 1% se tiver pontos
                        let p1Percentage = 50;
                        let p2Percentage = 50;
                        
                        if (totalPoints > 0) {
                          const p1Raw = (match.points_participant1 / totalPoints) * 100;
                          const p2Raw = (match.points_participant2 / totalPoints) * 100;
                          
                          // Se tem pontos, garantir mínimo de 1%
                          if (match.points_participant1 > 0 && p1Raw < 1) {
                            p1Percentage = 1;
                            p2Percentage = 99;
                          } else if (match.points_participant2 > 0 && p2Raw < 1) {
                            p1Percentage = 99;
                            p2Percentage = 1;
                          } else {
                            // Usar cálculo exato - NUNCA 50/50 se pontos são diferentes
                            if (match.points_participant1 !== match.points_participant2) {
                              p1Percentage = p1Raw;
                              p2Percentage = p2Raw;
                            } else {
                              // Só 50/50 se pontos realmente iguais
                              p1Percentage = 50;
                              p2Percentage = 50;
                            }
                          }
                        }
                        
                        const isFinalized = match.status === 'finalizado' || 
                                            match.status === 'wo' || 
                                            match.status === 'wo_both_disqualified' || 
                                            match.status === 'both_advance';

                        return (
                          <div key={match.match_id} className="border-2 rounded-lg overflow-hidden">
                            {/* Horário e Status no topo */}
                            {isEditing ? (
                              <div className="bg-gray-100 py-2 px-4 border-b space-y-2">
                                <Input
                                  value={editMatchData.battle_datetime}
                                  onChange={(e) => setEditMatchData({ ...editMatchData, battle_datetime: e.target.value })}
                                  placeholder="Ex: 15/01/2026 às 20:00"
                                  className="text-sm"
                                />
                                <select
                                  className="border rounded px-3 py-2 w-full"
                                  value={editMatchData.status}
                                  onChange={(e) => setEditMatchData({ ...editMatchData, status: e.target.value })}
                                >
                                  <option value="aguardando">Aguardando</option>
                                  <option value="finalizado">Finalizado</option>
                                </select>
                              </div>
                            ) : (
                              <div className="bg-gray-100 py-2 px-4 text-center border-b space-y-1">
                                {match.battle_datetime && (
                                  <p className="text-sm font-semibold text-gray-700">⏰ {match.battle_datetime}</p>
                                )}
                                <Badge
                                  className={
                                    match.status === 'finalizado'
                                      ? 'bg-green-600'
                                      : match.status === 'wo'
                                      ? 'bg-yellow-600'
                                      : match.status === 'wo_both_disqualified'
                                      ? 'bg-gray-600'
                                      : match.status === 'both_advance'
                                      ? 'bg-blue-600'
                                      : 'bg-gray-500'
                                  }
                                >
                                  {match.status === 'finalizado' 
                                    ? '✅ Finalizado' 
                                    : match.status === 'wo' 
                                    ? '⚠️ WO' 
                                    : match.status === 'wo_both_disqualified'
                                    ? '🚫 WO - Ambos Desclassificados'
                                    : match.status === 'both_advance'
                                    ? '🤝 Ambos Classificados'
                                    : '⏳ Aguardando'}
                                </Badge>
                              </div>
                            )}

                            {/* Card dividido em duas colunas */}
                            <div className="grid grid-cols-2 relative">
                              {/* Imagem VS no centro */}
                              <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
                                <img src="/vs-icon.png" alt="VS" className="w-20 h-20 drop-shadow-lg" />
                              </div>

                              {/* TIME AZUL - Lado Esquerdo */}
                              <div 
                                className="bg-blue-50 p-3 bg-cover bg-center relative"
                                style={{ backgroundImage: 'url(/bg-blue.png)' }}
                              >
                                {/* Overlay para melhor legibilidade */}
                                <div className="absolute inset-0 bg-blue-50/70"></div>
                                
                                <div className="relative z-10 flex flex-col items-center gap-2">
                                  {/* 
                                    ⚠️ PROPORÇÕES PERFEITAS - NÃO ALTERAR:
                                    - Container: w-32 h-32 (128px)
                                    - Avatar: w-[98px] h-[98px] (98px)
                                    - Moldura: absolute inset-0 com top: 2px
                                    - Proporção: Avatar = 76.5% do container
                                    - Centralização perfeita com flex items-center justify-center
                                  */}
                                  <div className="relative w-32 h-32 flex items-center justify-center">
                                    {/* Avatar centralizado - TAMANHO: 98px */}
                                    {p1Data?.avatar_url && (
                                      <img src={p1Data.avatar_url} alt="" className="w-[98px] h-[98px] rounded-full border-2 border-blue-400" />
                                    )}
                                    {/* Moldura 128px envolvendo avatar - DESLOCAMENTO: 2px para baixo */}
                                    {isFinalized && (match.winner_kako_id === p1Data?.kako_id || match.status === 'both_advance') && (
                                      <img src="/frame-winner.png" alt="Ganhou" className="absolute inset-0 w-full h-full object-contain pointer-events-none" style={{ top: '2px' }} />
                                    )}
                                    {isFinalized && match.winner_kako_id !== p1Data?.kako_id && match.status !== 'both_advance' && match.status !== 'wo_both_disqualified' && (
                                      <img src="/frame-loser.png" alt="Perdeu" className="absolute inset-0 w-full h-full object-contain pointer-events-none" style={{ top: '2px' }} />
                                    )}
                                  </div>
                                  <div className="text-center">
                                    <p className="font-bold text-blue-700">{p1Data?.nickname || 'Desconhecido'}</p>
                                    {/* Badge WO - antes do ID */}
                                    {(match.status === 'wo' || match.status === 'wo_both_disqualified' || match.status === 'both_advance') && (
                                      <Badge className="bg-yellow-600 text-xs mt-1">⚠️ W.O</Badge>
                                    )}
                                    <p className="text-xs text-gray-600 mt-1">#{p1Data?.kako_id}</p>
                                  </div>
                                </div>
                                {isEditing && (
                                  <div className="text-center relative z-10">
                                    <Input
                                      type="number"
                                      value={editMatchData.points_participant1 || ""}
                                      onChange={(e) => setEditMatchData({ ...editMatchData, points_participant1: parseInt(e.target.value) })}
                                      placeholder="Pontos"
                                      className="text-center"
                                    />
                                  </div>
                                )}
                              </div>

                              {/* TIME VERMELHO - Lado Direito */}
                              <div 
                                className="bg-red-50 p-4 space-y-3 bg-cover bg-center relative"
                                style={{ backgroundImage: 'url(/bg-red.png)' }}
                              >
                                {/* Overlay para melhor legibilidade */}
                                <div className="absolute inset-0 bg-red-50/70"></div>
                                
                                <div className="relative z-10 flex flex-col items-center gap-2">
                                  {/* 
                                    ⚠️ PROPORÇÕES PERFEITAS - NÃO ALTERAR:
                                    - Container: w-32 h-32 (128px)
                                    - Avatar: w-[98px] h-[98px] (98px)
                                    - Moldura: absolute inset-0 com top: 2px
                                    - Proporção: Avatar = 76.5% do container
                                    - Centralização perfeita com flex items-center justify-center
                                  */}
                                  <div className="relative w-32 h-32 flex items-center justify-center">
                                    {/* Avatar centralizado - TAMANHO: 98px */}
                                    {p2Data?.avatar_url && (
                                      <img src={p2Data.avatar_url} alt="" className="w-[98px] h-[98px] rounded-full border-2 border-red-400" />
                                    )}
                                    {/* Moldura 128px envolvendo avatar - DESLOCAMENTO: 2px para baixo */}
                                    {isFinalized && (match.winner_kako_id === p2Data?.kako_id || match.status === 'both_advance') && (
                                      <img src="/frame-winner.png" alt="Ganhou" className="absolute inset-0 w-full h-full object-contain pointer-events-none" style={{ top: '2px' }} />
                                    )}
                                    {isFinalized && match.winner_kako_id !== p2Data?.kako_id && match.status !== 'both_advance' && match.status !== 'wo_both_disqualified' && (
                                      <img src="/frame-loser.png" alt="Perdeu" className="absolute inset-0 w-full h-full object-contain pointer-events-none" style={{ top: '2px' }} />
                                    )}
                                  </div>
                                  <div className="text-center">
                                    <p className="font-bold text-red-700">{p2Data?.nickname || 'Desconhecido'}</p>
                                    {/* Badge WO - antes do ID */}
                                    {(match.status === 'wo' || match.status === 'wo_both_disqualified' || match.status === 'both_advance') && (
                                      <Badge className="bg-yellow-600 text-xs mt-1">⚠️ W.O</Badge>
                                    )}
                                    <p className="text-xs text-gray-600 mt-1">#{p2Data?.kako_id}</p>
                                  </div>
                                </div>
                                {isEditing && (
                                  <div className="text-center relative z-10">
                                    <Input
                                      type="number"
                                      value={editMatchData.points_participant2 || ""}
                                      onChange={(e) => setEditMatchData({ ...editMatchData, points_participant2: parseInt(e.target.value) })}
                                      placeholder="Pontos"
                                      className="text-center"
                                    />
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Barra Visual - Só aparece quando finalizado NORMAL (não WO) */}
                            {match.status === 'finalizado' && (
                              <div className="bg-gray-100 p-2 relative">
                                <div className="flex items-center justify-between gap-2 text-base font-bold mb-1">
                                  <span className="text-blue-600">{match.points_participant1}</span>
                                  <span className="text-red-600">{match.points_participant2}</span>
                                </div>
                                {/* X centralizado absolutamente */}
                                <div className="absolute top-2 left-1/2 transform -translate-x-1/2">
                                  <span className="text-gray-600 font-bold text-lg">×</span>
                                </div>
                                <div className="w-full h-6 bg-gray-200 rounded-full overflow-hidden flex">
                                  <div
                                    className="bg-blue-500 flex items-center justify-center text-white text-xs font-bold"
                                    style={{ width: `${p1Percentage}%` }}
                                  >
                                    {p1Percentage > 15 && `${Math.round(p1Percentage)}%`}
                                  </div>
                                  <div
                                    className="bg-red-500 flex items-center justify-center text-white text-xs font-bold"
                                    style={{ width: `${p2Percentage}%` }}
                                  >
                                    {p2Percentage > 15 && `${Math.round(p2Percentage)}%`}
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Botões de Ação no rodapé */}
                            <div className="bg-gray-50 p-3 border-t">
                              <div className="flex gap-2 justify-center">
                                {isEditing ? (
                                  <>
                                    <Button size="sm" onClick={() => saveMatch(match.match_id)}>
                                      <Save className="w-4 h-4 mr-1" />
                                      Salvar
                                    </Button>
                                    <Button size="sm" variant="outline" onClick={() => setEditingMatch(null)}>
                                      Cancelar
                                    </Button>
                                  </>
                                ) : (
                                  <>
                                    <Button size="sm" variant="outline" onClick={() => startEditMatch(match)}>
                                      <Edit className="w-4 h-4 mr-1" />
                                      Editar
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="destructive"
                                      onClick={() => setMatchWO(match.match_id, p1Data?.kako_id, p2Data?.kako_id)}
                                    >
                                      Marcar WO
                                    </Button>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </>
                  )}
                </>
              )}
            </TabsContent>

            {/* TAB: GANHADORES */}
            <TabsContent value="winners" className="space-y-4">
              <Tabs defaultValue="define" className="w-full">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="define">✏️ Definir</TabsTrigger>
                  <TabsTrigger value="current">🏆 Atuais</TabsTrigger>
                  <TabsTrigger value="history">📜 Edições Anteriores</TabsTrigger>
                </TabsList>

                {/* Sub-aba: Definir Ganhadores */}
                <TabsContent value="define" className="space-y-4 mt-4">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">✏️ Definir Ganhadores</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <Label>🥇 TOP 1 (KAKO ID)</Label>
                        <Input
                          value={config?.winner_top1_kako_id || ''}
                          onChange={(e) => setConfig({ ...config, winner_top1_kako_id: e.target.value })}
                          placeholder="ID do campeão"
                        />
                      </div>
                      <div>
                        <Label>🥈 TOP 2 (KAKO ID)</Label>
                        <Input
                          value={config?.winner_top2_kako_id || ''}
                          onChange={(e) => setConfig({ ...config, winner_top2_kako_id: e.target.value })}
                          placeholder="ID do vice-campeão"
                        />
                      </div>
                      <Button
                        onClick={async () => {
                          await updateConfig('winner_top1_kako_id', config.winner_top1_kako_id);
                          await updateConfig('winner_top2_kako_id', config.winner_top2_kako_id);
                          loadCurrentWinners();
                        }}
                        className="w-full"
                      >
                        💾 Salvar Ganhadores
                      </Button>
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Sub-aba: Ganhadores Atuais */}
                <TabsContent value="current" className="space-y-4 mt-4">
                  {!currentWinners ? (
                    <p className="text-gray-500 text-center py-8">Nenhum ganhador definido</p>
                  ) : (
                    <>
                      {/* Botões de Ação */}
                      <div className="flex gap-2">
                        <Button onClick={archiveWinners} variant="outline" className="flex-1">
                          📦 Arquivar Ganhadores
                        </Button>
                        <Button onClick={removeWinners} variant="destructive" className="flex-1">
                          🗑️ Remover Ganhadores
                        </Button>
                      </div>

                      {/* Cards dos Ganhadores */}
                      <Card className="border-4 border-yellow-400 bg-gradient-to-br from-yellow-50 to-yellow-100">
                        <CardContent className="p-6 text-center space-y-3">
                          <h3 className="text-3xl font-bold text-yellow-700">🥇 CAMPEÃO</h3>
                          {/* 
                            ⚠️ PROPORÇÕES PERFEITAS CAMPEÃO - NÃO ALTERAR:
                            - Container: w-[280px] h-[280px] (280px)
                            - Avatar: w-40 h-40 (160px)
                            - Proporção: 57% (avatar/container)
                            - Espaço moldura: 120px (60px cada lado)
                            - Moldura: frame-champion.png com top: 2px
                          */}
                          <div className="relative w-[280px] h-[280px] mx-auto flex items-center justify-center">
                            {currentWinners.top1?.avatar && (
                              <img src={currentWinners.top1.avatar} alt="" className="w-40 h-40 rounded-full border-4 border-yellow-500" />
                            )}
                            <img src="/frame-champion.png" alt="Campeão" className="absolute inset-0 w-full h-full object-contain pointer-events-none" style={{ top: '2px' }} />
                          </div>
                          <p className="text-2xl font-bold">{currentWinners.top1?.nickname || 'Desconhecido'}</p>
                          <p className="text-sm text-gray-600">ID: {currentWinners.top1?.kako_id}</p>
                          {currentWinners.top1?.prize && (
                            <p className="text-2xl font-bold text-green-600 mt-2">
                              💰 {currentWinners.top1.prize}
                            </p>
                          )}
                        </CardContent>
                      </Card>

                      {currentWinners.top2 && (
                        <Card className="border-4 border-gray-400 bg-gradient-to-br from-gray-50 to-gray-100">
                          <CardContent className="p-6 text-center space-y-3">
                            <h3 className="text-2xl font-bold text-gray-700">🥈 VICE-CAMPEÃO</h3>
                            {/* 
                              ⚠️ PROPORÇÕES PERFEITAS VICE - NÃO ALTERAR:
                              - Container: w-[280px] h-[280px] (280px)
                              - Avatar: w-40 h-40 (160px)
                              - Proporção: 57% (avatar/container)
                              - Espaço moldura: 120px (60px cada lado)
                              - Moldura: frame-vice.png com top: 2px
                              - IDÊNTICO ao Campeão!
                            */}
                            <div className="relative w-[280px] h-[280px] mx-auto flex items-center justify-center">
                              {currentWinners.top2?.avatar && (
                                <img src={currentWinners.top2.avatar} alt="" className="w-40 h-40 rounded-full border-4 border-gray-500" />
                              )}
                              <img src="/frame-vice.png" alt="Vice-Campeão" className="absolute inset-0 w-full h-full object-contain pointer-events-none" style={{ top: '2px' }} />
                            </div>
                            <p className="text-xl font-bold">{currentWinners.top2?.nickname || 'Desconhecido'}</p>
                            <p className="text-sm text-gray-600">ID: {currentWinners.top2?.kako_id}</p>
                            {currentWinners.top2?.prize && (
                              <p className="text-xl font-bold text-green-600 mt-2">
                                💰 {currentWinners.top2.prize}
                              </p>
                            )}
                          </CardContent>
                        </Card>
                      )}
                    </>
                  )}
                </TabsContent>

                {/* Sub-aba: Edições Anteriores */}
                <TabsContent value="history" className="space-y-3 mt-4">
                  {winnersHistory.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">Nenhum evento anterior</p>
                  ) : (
                    winnersHistory.map((event, idx) => (
                      <Card key={idx} className="border-2">
                        <CardHeader className="bg-purple-50">
                          <div className="flex items-center justify-between">
                            <CardTitle className="text-lg">🏆 {winnersHistory.length - idx}ª Edição - {event.event_date}</CardTitle>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => deleteHistoryEdition(event.edition_id, event.event_date)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </CardHeader>
                        <CardContent className="p-4 space-y-3">
                          <div className="flex items-center gap-3 bg-yellow-50 p-3 rounded-lg">
                            {event.winner_top1_avatar && (
                              <img src={event.winner_top1_avatar} alt="" className="w-16 h-16 rounded-full border-2 border-yellow-500" />
                            )}
                            <div className="flex-1">
                              <p className="font-bold">🥇 {event.winner_top1_nickname}</p>
                              <p className="text-sm text-yellow-700 font-semibold">1º LUGAR</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3 bg-gray-50 p-3 rounded-lg">
                            {event.winner_top2_avatar && (
                              <img src={event.winner_top2_avatar} alt="" className="w-14 h-14 rounded-full border-2 border-gray-400" />
                            )}
                            <div className="flex-1">
                              <p className="font-bold">🥈 {event.winner_top2_nickname}</p>
                              <p className="text-sm text-gray-600 font-semibold">2º LUGAR</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </TabsContent>
              </Tabs>
            </TabsContent>

            {/* TAB: REGRAS */}
            <TabsContent value="rules" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">📜 Regras da Batalha de PK</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="prose max-w-none">
                    <h3 className="text-xl font-bold mb-3">📋 Regras Gerais</h3>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Todos os participantes devem estar inscritos antes do início da competição</li>
                      <li>Cancelamentos devem ser feitos com antecedência</li>
                      <li>Faltas sem justificativa resultam em desclassificação (WO)</li>
                      <li>As batalhas seguem o sistema de eliminatória</li>
                    </ul>

                    <h3 className="text-xl font-bold mt-6 mb-3">⚔️ Durante as Batalhas</h3>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Cada batalha tem horário definido - respeite os horários</li>
                      <li>O vencedor é determinado pela maior pontuação</li>
                      <li>Em caso de empate, critérios de desempate serão aplicados</li>
                      <li>Comportamento antidesportivo pode resultar em desclassificação</li>
                    </ul>

                    <h3 className="text-xl font-bold mt-6 mb-3">🏆 Premiação</h3>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Os prêmios são definidos pela organização</li>
                      <li>TOP 1 e TOP 2 recebem premiação</li>
                      <li>A entrega dos prêmios segue as regras da plataforma</li>
                    </ul>

                    <h3 className="text-xl font-bold mt-6 mb-3">⚠️ Observações Importantes</h3>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>A organização se reserva o direito de alterar regras se necessário</li>

            {/* TAB: REGRAS - EDITAR */}
            <TabsContent value="rules" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">📜 Editar Regras do Evento</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Texto das Regras</Label>
                    <textarea
                      className="w-full min-h-[400px] border-2 rounded-lg p-4 font-mono text-sm"
                      value={config?.rules_text || ''}
                      onChange={(e) => setConfig({ ...config, rules_text: e.target.value })}
                      placeholder="Digite as regras do evento..."
                    />
                    <p className="text-xs text-gray-500 mt-2">
                      Use quebras de linha para organizar o texto. Será exibido exatamente como digitado.
                    </p>
                  </div>
                  <Button onClick={() => updateConfig('rules_text', config.rules_text)} className="w-full">
                    💾 Salvar Regras
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>

                      <li>Decisões dos administradores são finais</li>
                      <li>Mantenha o respeito com todos os participantes</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default PKBattleManagement;
